import { AnimatePresence, motion } from 'framer-motion';
import {
  Avatar,
  Box,
  IconButton,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PersonIcon from '@mui/icons-material/Person';
import { testimonials } from 'src/resources/data/feedbackData';

const Testimonials: React.FC = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [index, setIndex] = useState(0);
  const [expandedCard, setExpandedCard] = useState<number | null>(null);
  const cardsPerPage = isSmallScreen ? 1 : 2;
  const currentTestimonials = testimonials.slice(index, index + cardsPerPage);

  const handlePrev = () => {
    setIndex((prev) => Math.max(prev - cardsPerPage, 0));
    setExpandedCard(null); // collapse on page change
  };

  const handleNext = () => {
    setIndex((prev) =>
      Math.min(prev + cardsPerPage, testimonials.length - cardsPerPage)
    );
    setExpandedCard(null); // collapse on page change
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((prev) =>
        prev + cardsPerPage >= testimonials.length ? 0 : prev + cardsPerPage
      );
      setExpandedCard(null);
    }, 7000); // 7-second auto-advance

    return () => clearInterval(interval);
  }, [cardsPerPage]);

  return (
    <Box sx={{ textAlign: 'center', py: 8 }}>
      {/* Title */}
      <Typography
        sx={{
          fontFamily: 'Poppins, sans-serif',
          fontSize: '2rem',
          fontWeight: 200,
          textAlign: 'center',
          color: '#333',
          mb: 3,
        }}
      >
        Happy Clients
      </Typography>

      {/* Underline */}
      <Box
        sx={{
          width: 80,
          height: 2,
          backgroundColor: '#ed7186',
          mx: 'auto',
          mb: 6,
        }}
      />

      {/* Testimonials Grid with Animation */}
      <Box sx={{ position: 'relative', overflow: 'hidden' }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ duration: 0.5 }}
            style={{
              display: 'grid',
              gridTemplateColumns: isSmallScreen ? '1fr' : '1fr 1fr',
              gap: 32,
              maxWidth: '1200px',
              margin: '0 auto',
              paddingLeft: 16,
              paddingRight: 16,
              paddingTop: 4,
              paddingBottom: 4
            }}
          >
            {currentTestimonials.map((testimonial, idx) => {
              const isExpanded = expandedCard === idx;

              return (
                <Paper
                  key={idx}
                  elevation={2}
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    borderRadius: 2,
                    border: '1px solid #eee',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease-in-out',
                  }}
                  onClick={() =>
                    setExpandedCard((prev) => (prev === idx ? null : idx))
                  }
                >
                  {/* Avatar with user icon */}
                  <Avatar
                    sx={{
                      width: 72,
                      height: 72,
                      mb: 2,
                      bgcolor: '#ccc',
                    }}
                  >
                    <PersonIcon fontSize="large" />
                  </Avatar>

                  {/* Customer Name */}
                  <Typography
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '1.125rem',
                      fontWeight: 500,
                      mb: 1,
                    }}
                  >
                    {testimonial.name}
                  </Typography>

                  {/* Feedback (clamp or expand) */}
                  <Typography
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '1rem',
                      fontStyle: 'italic',
                      lineHeight: 1.8,
                      color: 'text.secondary',
                      maxWidth: '90%',
                      overflow: 'hidden',
                      display: '-webkit-box',
                      WebkitLineClamp: isExpanded ? 'none' : 2,
                      WebkitBoxOrient: 'vertical',
                      textOverflow: 'ellipsis',
                      whiteSpace: isExpanded ? 'normal' : 'initial',
                      transition: 'all 0.3s',
                    }}
                  >
                    “{testimonial.feedback}”
                  </Typography>

                  {/* Toggle link */}
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      color: '#ed7186',
                      fontWeight: 500,
                      textDecoration: 'underline',
                      fontSize: '0.9rem',
                    }}
                  >
                    {isExpanded ? 'Show less' : 'Read more'}
                  </Typography>
                </Paper>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </Box>

      {/* Navigation Buttons */}
      <Box sx={{ pt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
        <IconButton onClick={handlePrev} disabled={index === 0}>
          <ArrowBackIcon fontSize="large" />
        </IconButton>
        <IconButton
          onClick={handleNext}
          disabled={index + cardsPerPage >= testimonials.length}
        >
          <ArrowForwardIcon fontSize="large" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default Testimonials;
